import { type ReactNode, useId } from 'react';
import {
    type Control,
    Controller,
    type FieldPath,
    type FieldValues,
    type RegisterOptions,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import TextField from '@/components/atoms/TextField';

import FormField from './FormField';

interface FormTextFieldProps<TFieldValues extends FieldValues> {
    control: Control<TFieldValues>;
    name: FieldPath<TFieldValues>;
    label: string;
    placeholder?: string;
    rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
    errorMessage?: string;
    optional?: boolean;
    /** Inline affordance on the label row — see `FormField`. */
    labelAction?: ReactNode;
}

export default function FormTextField<TFieldValues extends FieldValues>(
    props: FormTextFieldProps<TFieldValues>
) {
    const { control, name, label, placeholder, rules, errorMessage, optional, labelAction } = props;
    const id = useId();
    const { t } = useTranslation('fishGroups');

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field, fieldState }) => (
                <FormField
                    htmlFor={id}
                    label={label}
                    optional={optional}
                    labelAction={labelAction}
                    error={
                        fieldState.error
                            ? fieldState.error.message ||
                              errorMessage ||
                              t('actions.errors.required')
                            : null
                    }>
                    <TextField
                        id={id}
                        type="text"
                        placeholder={placeholder}
                        invalid={Boolean(fieldState.error)}
                        name={field.name}
                        value={(field.value as string | undefined) ?? ''}
                        onBlur={field.onBlur}
                        onChange={(e) => field.onChange(e.target.value)}
                    />
                </FormField>
            )}
        />
    );
}
